@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.billing.chatAboutRules
import com.royceecoleman.bill.billing.getBillingRules
import com.royceecoleman.bill.billing.getWordingChecks
import com.royceecoleman.bill.billing.restoreRulesVersion
import com.royceecoleman.bill.billing.saveBillingRules
import com.royceecoleman.bill.billing.saveWordingChecks
import com.royceecoleman.bill.billing.suggestWordingChecks
import com.royceecoleman.bill.billing.undoRulesChange
import com.royceecoleman.bill.billing.uploadBillingRules
import com.royceecoleman.bill.shared.billing.BillingRules
import com.royceecoleman.bill.shared.billing.RulesChatRequest
import com.royceecoleman.bill.shared.billing.RulesChatTurn
import com.royceecoleman.bill.shared.billing.RulesUpdate
import com.royceecoleman.bill.shared.billing.RulesUpload
import com.royceecoleman.bill.shared.billing.WordingCheck
import com.royceecoleman.bill.shared.billing.WordingChecks
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

internal data class RulesDiffLine(
    val kind: Kind,
    val text: String,
) {
    enum class Kind { SAME, REMOVED, ADDED }
}

/** A line diff of two rule sets: unchanged ends are kept, and the changed middle is aligned by longest common subsequence. */
internal fun rulesLineDiff(
    before: String,
    after: String,
): List<RulesDiffLine> {
    val old = if (before.isEmpty()) emptyList() else before.split("\n")
    val new = if (after.isEmpty()) emptyList() else after.split("\n")
    var start = 0
    while (start < old.size && start < new.size && old[start] == new[start]) start++
    var end = 0
    while (end < old.size - start && end < new.size - start && old[old.size - 1 - end] == new[new.size - 1 - end]) end++
    val a = old.subList(start, old.size - end)
    val b = new.subList(start, new.size - end)
    val middle = mutableListOf<RulesDiffLine>()
    if (a.size.toLong() * b.size > 250_000) {
        // Too large to align cheaply: show every changed line as removed, then added.
        a.mapTo(middle) { RulesDiffLine(RulesDiffLine.Kind.REMOVED, it) }
        b.mapTo(middle) { RulesDiffLine(RulesDiffLine.Kind.ADDED, it) }
    } else {
        val common = Array(a.size + 1) { IntArray(b.size + 1) }
        for (i in a.indices.reversed()) {
            for (j in b.indices.reversed()) {
                common[i][j] = if (a[i] == b[j]) common[i + 1][j + 1] + 1 else maxOf(common[i + 1][j], common[i][j + 1])
            }
        }
        var i = 0
        var j = 0
        while (i < a.size || j < b.size) {
            when {
                i < a.size && j < b.size && a[i] == b[j] -> {
                    middle += RulesDiffLine(RulesDiffLine.Kind.SAME, a[i++])
                    j++
                }
                j >= b.size || (i < a.size && common[i + 1][j] >= common[i][j + 1]) -> middle += RulesDiffLine(RulesDiffLine.Kind.REMOVED, a[i++])
                else -> middle += RulesDiffLine(RulesDiffLine.Kind.ADDED, b[j++])
            }
        }
    }
    return old.take(start).map { RulesDiffLine(RulesDiffLine.Kind.SAME, it) } + middle + old.takeLast(end).map { RulesDiffLine(RulesDiffLine.Kind.SAME, it) }
}

private fun rulesStamp(value: Long): String = Date(value.toDouble()).toISOString().take(16).replace('T', ' ') + " UTC"

private val sources = mapOf("USER" to "Edited by you", "AGENT" to "Changed by BILL", "UPLOAD" to "Upload", "UNDO" to "Undo", "RESTORE" to "Restored")

/**
 * The lawyer's standing rules: edit them directly, ask BILL to change them or replace them with a PDF's or Word document's text. BILL's
 * changes and uploads are saved at once and shown as a diff with Undo; the conversation lives only on this page.
 */
@Composable
internal fun RulesPage(
    busy: Boolean,
    act: (suspend () -> Unit) -> Unit,
    setDirty: (Boolean) -> Unit,
    notify: (String) -> Unit,
) {
    var rules by remember { mutableStateOf<BillingRules?>(null) }
    var text by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var conversation by remember { mutableStateOf(listOf<RulesChatTurn>()) }
    var change by remember { mutableStateOf<RulesUpdate?>(null) }
    var savedChecks by remember { mutableStateOf<WordingChecks?>(null) }
    var checks by remember { mutableStateOf(listOf<WordingCheck>()) }
    var suggestions by remember { mutableStateOf(listOf<WordingCheck>()) }

    // Unsaved rule text and unsaved checks both guard navigation away from the page.
    fun markDirty(rulesText: String = text) = setDirty(rulesText != rules?.text.orEmpty() || checks != savedChecks?.checks.orEmpty())

    fun apply(update: RulesUpdate) {
        rules = update.rules
        text = update.rules.text
        markDirty()
    }

    fun applyChecks(loaded: WordingChecks) {
        savedChecks = loaded
        checks = loaded.checks
        markDirty()
    }

    fun reload() =
        act {
            val loaded = getBillingRules()
            rules = loaded
            text = loaded.text
            applyChecks(getWordingChecks())
        }
    LaunchedEffect(Unit) { reload() }

    Eyebrow("STANDING RULES")
    H1 { Text("Your rules for every draft.") }
    P({ classes("bill-muted") }) {
        Text(
            "BILL follows these rules in every entry it drafts, for every client, alongside each client's instructions. They are sent at the start and again at the end of every draft request, so they never fade from view. Rules shape wording and style; they can never make BILL invent facts, dates or time.",
        )
    }
    val current = rules
    if (current == null) {
        P { Text("Loading your rules…") }
        if (!busy) BillButton("Retry") { reload() }
        return
    }
    val dirty = text != current.text
    val tooLong = text.length > BillingRules.MAX_LENGTH
    Div({ classes("bill-card") }) {
        BillArea(
            "Standing rules",
            text,
            "1. Begin each entry with a verb.\n2. Write \"Review and analyze\", never \"Rev\".\n3. Name the document reviewed.",
            "rules",
        ) {
            text = it
            markDirty(it)
        }
        P({
            classes(if (tooLong) "bill-alert" else "bill-muted")
            attr("aria-live", "polite")
        }) {
            Text(
                "${BillingRules.thousands(text.length)} / ${BillingRules.MAX_LENGTH_TEXT} characters" +
                    if (current.revision > 0) " · Revision ${current.revision}, saved ${rulesStamp(current.updatedAt)}" else " · Not saved yet",
            )
        }
        Div({ classes("bill-row") }) {
            BillButton("Save rules", busy || !dirty || tooLong, "primary") {
                act {
                    apply(saveBillingRules(current.revision, text))
                    change = null
                    notify("Rules saved. Your next draft follows them.")
                }
            }
            if (dirty) {
                BillButton("Discard changes", busy) {
                    text = current.text
                    markDirty(current.text)
                }
            }
            BillButton("Reload rules", busy) {
                if (!dirty || window.confirm("Discard your unsaved rule edits and reload?")) reload()
            }
        }
        Label(attrs = { classes("bill-field") }) {
            Text("Upload rules (PDF or Word)")
            Input(InputType.File, attrs = {
                attr("accept", DOCUMENT_ACCEPT)
                attr("aria-describedby", "bill-rules-upload-help")
                if (busy) attr("disabled", "")
                onChange { event ->
                    val input = event.target
                    val file = input.files?.item(0) ?: return@onChange
                    if (dirty && !window.confirm("Discard your unsaved rule edits and replace your rules with this document?")) {
                        input.value = ""
                        return@onChange
                    }
                    act {
                        try {
                            rulesFileRefusal(file.picked())?.let { throw IllegalArgumentException(it) }
                            val update = uploadBillingRules(RulesUpload(current.revision, file.name, readFileBase64(file)))
                            apply(update)
                            change = update.takeIf { it.changed }
                            notify(
                                if (update.changed) {
                                    "Your rules now come from ${file.name}. Review the change below; Undo restores the previous version."
                                } else {
                                    "That document matches your current rules, so nothing changed."
                                },
                            )
                        } finally {
                            input.value = ""
                        }
                    }
                }
            })
        }
        P({
            classes("bill-muted")
            id("bill-rules-upload-help")
        }) { Text("Replaces your current rules with the text of a PDF or Word (.docx) document. Earlier versions stay in History.") }
    }

    Div({ classes("bill-card") }) {
        H2 { Text("Ask BILL to change your rules") }
        P({ classes("bill-muted") }) {
            Text(
                "Describe a change, such as \"never use the word 'attention'\", or ask a question. BILL saves its changes right away and shows exactly what changed, with Undo. Uses no drafting credits · ${current.chatsRemaining} chats left today.",
            )
        }
        if (conversation.isNotEmpty()) {
            Div({
                attr("role", "log")
                attr("aria-label", "Rules conversation")
            }) {
                conversation.forEach { turn ->
                    P({ classes("bill-chat-turn") }) {
                        Span({ classes("bill-chat-who") }) { Text(if (turn.role == "user") "You: " else "BILL: ") }
                        Text(turn.content)
                    }
                }
            }
        }
        BillArea("Message to BILL about your rules", message, "e.g. Add a rule to spell out every abbreviation.") { message = it }
        if (dirty) P({ classes("bill-muted") }) { Text("Save or discard your edits first. BILL works on your saved rules.") }
        BillButton("Send to BILL", busy || dirty || message.isBlank() || message.trim().length > BillingRules.MAX_MESSAGE || current.chatsRemaining <= 0, "primary") {
            act {
                val sent = message.trim()
                val update = chatAboutRules(RulesChatRequest(current.revision, sent, conversation.takeLast(6)))
                conversation = (conversation + RulesChatTurn("user", sent) + RulesChatTurn("assistant", update.reply)).takeLast(20)
                message = ""
                apply(update)
                if (update.changed) {
                    change = update
                    notify("BILL changed your rules. Review the change below; Undo restores the previous version.")
                }
            }
        }
    }

    WordingChecksCard(
        busy = busy,
        checks = checks,
        dirty = checks != savedChecks?.checks.orEmpty(),
        suggestions = suggestions,
        canSuggest = current.text.isNotBlank(),
        chatsRemaining = current.chatsRemaining,
        change = {
            checks = it
            markDirty()
        },
        changeSuggestions = { suggestions = it },
        save = {
            act {
                val trimmed = checks.filter { it.pattern.isNotBlank() }
                applyChecks(saveWordingChecks(savedChecks?.revision ?: 0, trimmed))
                notify("Wording checks saved. Review applies them to every entry right away.")
            }
        },
        discard = {
            checks = savedChecks?.checks.orEmpty()
            markDirty()
        },
        suggest = {
            act {
                val result = suggestWordingChecks()
                rules = rules?.copy(chatsRemaining = result.chatsRemaining)
                val fresh = result.suggestions.filterNot { new -> checks.any { it.pattern.equals(new.pattern, ignoreCase = true) } }
                suggestions = fresh
                notify(
                    if (fresh.isEmpty()) {
                        "BILL found no new wording to check in your rules."
                    } else {
                        "BILL suggested ${fresh.size} checks. Add the ones you want, edit them if needed, then save."
                    },
                )
            }
        },
    )

    change?.let { shown ->
        Div({
            classes("bill-card")
            attr("role", "region")
            attr("aria-label", "Latest rules change")
        }) {
            H2 { Text("What changed") }
            P { Text("${shown.summary.ifBlank { "Rules changed" }} · revision ${shown.rules.revision}") }
            Div({ classes("bill-diff") }) {
                rulesLineDiff(shown.previousText, shown.rules.text).forEach { line ->
                    when (line.kind) {
                        RulesDiffLine.Kind.SAME -> Div { Text("  ${line.text}") }
                        RulesDiffLine.Kind.REMOVED -> DiffLine("bill-diff-removed", "−", "Removed: ", line.text)
                        RulesDiffLine.Kind.ADDED -> DiffLine("bill-diff-added", "+", "Added: ", line.text)
                    }
                }
            }
            Div({ classes("bill-row") }) {
                BillButton("Undo this change", busy || current.revision != shown.rules.revision) {
                    act {
                        val undone = undoRulesChange(current.revision)
                        apply(undone)
                        change = null
                        notify("Change undone. Your previous rules are back as revision ${undone.rules.revision}.")
                    }
                }
                BillButton("Hide", busy) { change = null }
            }
        }
    }

    BillDetails("History (${current.history.size} versions)") {
        P({ classes("bill-muted") }) { Text("BILL keeps your 50 most recent versions. Restoring one saves it as a new version, so it can be undone too.") }
        current.history.forEach { version ->
            Div({ classes("bill-row", "bill-between") }) {
                Span {
                    Text("Revision ${version.revision} · ${rulesStamp(version.createdAt)} · ${sources[version.source] ?: version.source}${if (version.summary.isBlank()) "" else " · ${version.summary}"}")
                }
                if (version.revision == current.revision) {
                    Span({ classes("bill-pill") }) { Text("Current") }
                } else {
                    BillButton("Restore revision ${version.revision}", busy || dirty) {
                        if (window.confirm("Restore revision ${version.revision}? Your current rules stay in this history.")) {
                            act {
                                val restored = restoreRulesVersion(current.revision, version.revision)
                                apply(restored)
                                change = restored.takeIf { it.changed }
                                notify("Revision ${version.revision} restored.")
                            }
                        }
                    }
                }
            }
        }
    }
}

/** One changed line: the sign is visual only; screen readers hear "Removed:" or "Added:" instead. */
@Composable
private fun DiffLine(
    kind: String,
    sign: String,
    spoken: String,
    text: String,
) {
    Div({ classes(kind) }) {
        Span({ attr("aria-hidden", "true") }) { Text("$sign ") }
        Span({ classes("bill-sr-only") }) { Text(spoken) }
        Text(text)
    }
}
